import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { announce } from '@atlaskit/pragmatic-drag-and-drop-live-region';
import {
  AssetDTO,
  canNestUnder,
  CreateFormRequestDTO,
  DropdownFormElement,
  EntityRefUtil,
  FormDTO,
  FormElement,
  FormElementKind,
  FormType,
  PermissionRoles,
  reconcileShowFieldRule,
  RecordState,
  removeValueFromRules,
  TableFormElement,
  UpdateFormRequestDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Highlight, themes } from 'prism-react-renderer';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { FormRevisionHistory } from '@/components/form-revisions/form-revision-history';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { useConditionalLogicEnabled } from '@/hooks/use-conditional-logic-enabled';
import { useDeletedLogicValues } from '@/hooks/use-deleted-logic-values';
import { usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/lib/utils';
import { getAssetsQueryOptions } from '@/queries/assets';
import { formKeys, getFormByIdQueryOptions, inspectionFormKeys } from '@/queries/forms';
import { repairFormKeys } from '@/queries/repairs';
import { scheduleFormKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { useWorkspaceNavigate } from '@/utils/routes';

import { SaveChangesDialog } from '../common/save-changes-dialog';
import ArchiveIcon from '../icons/archive';
import ClockIcon from '../icons/clock';
import CloneIcon from '../icons/clone';
import CloseIcon from '../icons/close';
import DeleteIcon from '../icons/delete';
import EllipsisHorizontalIcon from '../icons/ellipsis-horizontal';
import FormChecklistIcon from '../icons/form-checklist';
import FormTemplateIcon from '../icons/form-template';
import GripDotVerticalIcon from '../icons/grip-dot-vertical';
import InformationIcon from '../icons/information';
import ListIcon from '../icons/list';
import LogicIcon from '../icons/logic';
import UndoIcon from '../icons/undo';
import WarningIcon from '../icons/warning';
import WarningTriangleIcon from '../icons/warning-triangle';
import { FormTopbar } from '../topbar/form-topbar';
import { useFormTopbarStore } from '../topbar/form-topbar-store';
import { AutoResizeInput } from '../ui/auto-resize-input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { LogicRuleConfigLabels } from './config-field-labels';
import { DropdownLogicRuleBuilder } from './elements/dropdown-logic-rule-builder';
import { initializeTableInitialValues } from './elements/table';
import {
  CanvasDraggableCard,
  CanvasDropZone,
  FormDesignerDndProvider,
  resolveReorderMove,
  RevealDropZone,
  ToolboxDraggableItem,
  useFormDesignerDndStable,
  type DesignerDrop,
  type MoveDirection,
} from './form-designer-dnd';
import { FormDesignerElements, type FormDesignerElementDefinition } from './form-designer.elements';
import type { FormDesignerForm } from './form-designer.types';
import {
  boxSlotToFlatInsertIndex,
  buildDroppedElement,
  canDropCardIntoRuleBox,
  clonedElement,
  cloneRuleSubtree,
  definitionFor,
  deletedStaticOptionValues,
  elementsAfterDelete,
  flatCanvasIndexToArrayIndex,
  loadableElements,
  orderElementsByTrigger,
  revealReorderFlatIndices,
  validateForm,
} from './form-designer.utils';
import { FormElementsRenderer, FormRendererData } from './form-elements-renderer';

// Stable empty default for the errored-rule prop, so an un-flagged subtree doesn't get a fresh Set each render.
const EMPTY_RULE_IDS: ReadonlySet<string> = new Set();

type ConfigPanelProps = {
  formElement: FormElement | null;
  onUpdateConfig: (id: string, updates: Partial<Omit<FormElement, 'kind'>>) => void;
};

/**
 * Determines if an IssueSelect element should be hidden based on form type.
 * Used consistently across the form designer (toolbox, canvas, and draggable items).
 */
function shouldHideIssueSelect(elementKind: FormElementKind, formType: number): boolean {
  return elementKind === FormElementKind.IssueSelect && formType === FormType.RepairForm;
}

export function FormDesigner() {
  const toast = useToast();
  const { formId } = useParams<{ formId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useWorkspaceNavigate();
  const { user } = useUserStore();
  const { isAdmin } = usePermissions();

  // Members can't manage form templates of any kind. The route is
  // `/forms/:formId` (or `/forms/create`); bounce them back to the
  // dashboard so they can navigate from a register they have access to.
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAdmin, navigate]);

  const initialFormType = searchParams.get('formType');

  const [openJSONPreview, setOpenJSONPreview] = useState(false);
  const [openSaveChanges, setOpenSaveChanges] = useState(false);
  // A trigger delete that cascade-removes revealed questions is destructive and unrecoverable, so it waits
  // on a confirm; `revealedCount` drives the prompt's copy. A plain-field delete sets this null and runs
  // straight through.
  const [pendingCascadeDelete, setPendingCascadeDelete] = useState<{ index: number; revealedCount: number } | null>(null);

  const { data: existingForm } = useQuery(getFormByIdQueryOptions({ formId }));
  const queryClient = useQueryClient();

  const createFormMutation = useMutation({
    mutationFn: async (data: CreateFormRequestDTO) => {
      return await (await ApiService.CreateForm(data)).json();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create form');
    },
  });

  const updateFormMutation = useMutation({
    mutationFn: async (data: UpdateFormRequestDTO) => {
      if (!formId) throw new Error('No form ID provided');
      return await (await ApiService.UpdateForm(formId, data)).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.all });
      queryClient.invalidateQueries({ queryKey: inspectionFormKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
      queryClient.invalidateQueries({ queryKey: repairFormKeys.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update form');
    },
  });

  const cloneFormMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error('No form ID provided');
      return await (await ApiService.CloneForm(formId)).json();
    },
    onSuccess: (data) => {
      toast.success('Form cloned successfully');
      navigate(`/forms/${data.form.id}`);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to clone form');
    },
  });

  const archiveFormMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error('No form ID provided');
      return await ApiService.BulkUpdateFormState({ ids: [formId], state: RecordState.Archived });
    },
    onSuccess: () => {
      toast.success('Form archived successfully');
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to archive form');
    },
  });

  const restoreFormMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error('No form ID provided');
      return await ApiService.BulkUpdateFormState({ ids: [formId], state: RecordState.Active });
    },
    onSuccess: () => {
      toast.success('Form restored successfully');
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to restore form');
    },
  });

  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const form = useForm<FormDesignerForm>({
    defaultValues: {
      name: 'Untitled Form',
      type: initialFormType ? parseInt(initialFormType) : FormType.InspectionForm,
      elements: [],
    },
  });

  const {
    watch,
    setValue,
    formState: { isDirty },
  } = form;
  const formElements = watch('elements');
  const name = watch('name');
  const formType = watch('type');

  // The deleted-list-value half of invalid-rule surfacing: needs the fetched list options, so it lives here in
  // the React layer rather than in the pure validateForm. These flags gate the save below; `deletedByRuleId`
  // flags the specific offending rows.
  const { hasDeletedValues, deletedByRuleId, isResolving: logicValuesResolving, hasUnverifiableList } = useDeletedLogicValues(formElements);

  function handleClose() {
    if (isDirty) {
      setOpenSaveChanges(true);
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard');
      }
    }
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (form.formState.isDirty) {
          setOpenSaveChanges(true);
        } else {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/dashboard');
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, setOpenSaveChanges, form.formState.isDirty]);

  // Keep RHF's generated key off the `id` field — the default keyName: 'id' overwrites each element's own
  // stable id with a key it regenerates on every setValue('elements', …). Selection tracks the element id,
  // so that clobbering made editing a field (e.g. adding a dropdown option) reset the sidebar selection.
  const { fields, move, insert } = useFieldArray({
    control: form.control,
    name: 'elements',
    keyName: '_key',
  });

  const usedElements = useMemo(() => formElements.map((el) => el.kind), [formElements]);

  const conditionalLogicEnabled = useConditionalLogicEnabled(formType);
  // Ids currently revealed by a rule — rendered inside their trigger's box, excluded from the flat canvas.
  const revealedIds = useMemo(
    () => new Set(conditionalLogicEnabled ? formElements.filter((el) => el.dependsOn).map((el) => el.id) : []),
    [formElements, conditionalLogicEnabled],
  );
  // The flat canvas order (revealed children excluded) — the index space the reorder/create drops resolve in.
  const orderedCardIds = useMemo(() => formElements.filter((el) => !revealedIds.has(el.id)).map((el) => el.id), [formElements, revealedIds]);
  // Cards that sit in `orderedCardIds` but the canvas doesn't render (an IssueSelect hidden on a RepairForm).
  // The pointer path never targets them (they're not drop targets); the keyboard path must step over them so an
  // arrow move lands on the next VISIBLE card and the announced "position N of M" counts only visible cards.
  const hiddenCanvasIds = useMemo(
    () => new Set(formElements.filter((el) => shouldHideIssueSelect(el.kind, formType)).map((el) => el.id)),
    [formElements, formType],
  );

  // Whether a card may be dropped into a given rule's box: reject when it would cycle (the card is the box's
  // trigger or an ancestor of it) or push the moved subtree past the nesting cap. The DnD monitor consults
  // this at drop time (it holds no element tree of its own).
  const revealDropGuard = useCallback((sourceId: string, triggerId: string) => canDropCardIntoRuleBox(formElements, sourceId, triggerId), [formElements]);

  // Which triggers have their conditional-logic block open, keyed by the element's OWN stable id. Held here
  // (not in the row) because a card moving between the flat canvas and a reveal box remounts at its new
  // position, which would reset row-local open state and collapse the block mid-edit. A trigger with a saved
  // rule counts as open too, so a reload shows it.
  const [openLogicElementIds, setOpenLogicElementIds] = useState<Set<string>>(new Set());
  const setLogicOpen = (elementId: string, open: boolean) =>
    setOpenLogicElementIds((prev) => {
      const next = new Set(prev);
      if (open) next.add(elementId);
      else next.delete(elementId);
      return next;
    });

  // Null until a save attempt fails; the snapshot of that attempt then drives the inline red flags, cleared on a
  // successful save. `ruleIdsAtSave` are the rules that existed at the failed save — drives ONLY the dropzone "no
  // fields to show" flag (a rule added after the failed save stays clean until the next save). `conflictingRuleIds`
  // are the same-operator value conflicts from validateForm. Both are snapshots so rows aren't flagged mid-edit.
  const [logicErrors, setLogicErrors] = useState<{ ruleIdsAtSave: Set<string>; conflictingRuleIds: Set<string> } | null>(null);
  // The red value-control flag: snapshot conflict (from the failed save) plus live deleted-list-value (from the
  // hook). Incompleteness is NOT in here — it's read live per-row via `ruleHasNoValues`, so that red clears the
  // instant the admin picks a value rather than lingering until the next save attempt.
  const flaggedRuleIds = useMemo(() => new Set<string>([...(logicErrors?.conflictingRuleIds ?? []), ...deletedByRuleId]), [logicErrors, deletedByRuleId]);

  const getFormTypeIcon = (formType: number) => {
    const iconProps = { className: 'bg-grey-2 rounded-md p-1', size: 40 };
    return match(formType)
      .with(FormType.InspectionForm, () => <FormChecklistIcon {...iconProps} />)
      .with(FormType.ScheduleForm, () => <WarningTriangleIcon {...iconProps} />)
      .with(FormType.RepairForm, () => <ClockIcon {...iconProps} />)
      .otherwise(() => <FormTemplateIcon {...iconProps} />);
  };

  const getFormTypeDescription = (formType: number) => {
    return match(formType)
      .with(FormType.InspectionForm, () => 'Inspection Form')
      .with(FormType.ScheduleForm, () => 'Schedule Form')
      .with(FormType.RepairForm, () => 'Repair Form')
      .otherwise(() => 'Form Template');
  };

  useLayoutEffect(() => {
    useFormTopbarStore.getState().setInfo(
      <div className="flex h-[55px] w-full items-center gap-[7px]">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div>{getFormTypeIcon(formType)}</div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="bottom" sideOffset={0} className="bg-default-text pointer-events-auto text-xs font-medium text-white">
                <TooltipArrow className="fill-default-text" />
                {getFormTypeDescription(formType)}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
        <div className="mt-[1px] flex flex-col gap-[1px]">
          <div className="flex items-center gap-1">
            <AutoResizeInput
              value={name}
              onChange={(e) => {
                setValue('name', e.target.value, { shouldDirty: true });
              }}
              placeholder="Untitled Form"
              className="hover:ring-grey-3 focus-visible:ring-default-blue h-auto max-w-[calc(70vw)] border-none bg-transparent px-1 py-0 text-xl font-medium hover:ring-2 focus-visible:ring-2"
            />
            {existingForm && <div className="text-secondary-text mt-[1px] ml-[1px] text-xs">{EntityRefUtil.createFormRef(existingForm?.form.number)}</div>}
            {existingForm?.form.state === RecordState.Archived && (
              <Badge variant="orange" size="sm">
                <div className="mt-[1px] flex gap-[2px]">
                  <ArchiveIcon size={15} />
                  <span className="text-xs font-medium">Archived</span>
                </div>
              </Badge>
            )}
          </div>
          <div className="flex h-[25px] items-end gap-2">
            <Button
              type="button"
              className={`ml-1 h-[25px] rounded-none rounded-t-md px-[5px] py-2 text-sm`}
              variant={!isPreviewOpen ? 'default' : 'ghost'}
              onClick={() => setIsPreviewOpen(false)}>
              Edit
            </Button>
            <Button
              type="button"
              className={`h-[25px] rounded-none rounded-t-md px-1 py-2 text-sm`}
              variant={isPreviewOpen ? 'default' : 'ghost'}
              onClick={() => setIsPreviewOpen(true)}>
              Preview
            </Button>
          </div>
        </div>
      </div>,
    );

    useFormTopbarStore.getState().setActions(
      <div className="flex items-center gap-1">
        <Button type="button" variant="ghost" className="mr-[1px]" aria-label="Timeline" onClick={() => setIsActivityOpen(true)}>
          <ListIcon />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" aria-label="More actions" className="mr-[1px]">
              <EllipsisHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          {existingForm?.form.state === RecordState.Active ? (
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="cursor-pointer" onClick={() => cloneFormMutation.mutate()} disabled={!formId || cloneFormMutation.isPending}>
                <CloneIcon className="mr-1" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => archiveFormMutation.mutate()} disabled={!formId || archiveFormMutation.isPending}>
                <ArchiveIcon className="mr-1" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          ) : (
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="cursor-pointer" onClick={() => restoreFormMutation.mutate()} disabled={!formId || restoreFormMutation.isPending}>
                <UndoIcon className="mr-1" />
                Restore
              </DropdownMenuItem>
            </DropdownMenuContent>
          )}
        </DropdownMenu>
        <Button
          className="mx-[1px] font-medium"
          type="submit"
          variant="success"
          disabled={createFormMutation.isPending || updateFormMutation.isPending || !isDirty}>
          Save
        </Button>
        <Button type="button" variant="ghost" aria-label="Close" className="mr-[1px]" onClick={handleClose}>
          <CloseIcon />
        </Button>
      </div>,
    );

    return () => {
      useFormTopbarStore.getState().setActions(<></>);
      useFormTopbarStore.getState().setActions(null);
    };
  }, [
    isPreviewOpen,
    formId,
    navigate,
    form,
    user,
    name,
    setOpenJSONPreview,
    setIsPreviewOpen,
    createFormMutation.isPending,
    updateFormMutation.isPending,
    cloneFormMutation.isPending,
    archiveFormMutation.isPending,
    restoreFormMutation.isPending,
    formType,
    existingForm,
    cloneFormMutation,
    archiveFormMutation,
    restoreFormMutation,
    setValue,
    isDirty,
  ]);

  useEffect(() => {
    if (existingForm?.form) {
      const loadedForm = existingForm.form;
      form.reset({
        name: loadedForm.name,
        type: loadedForm.type,
        // definitionFor throws on an unknown kind, so this filter is what keeps a
        // form saved with a deprecated kind loadable instead of crashing.
        elements: loadableElements(loadedForm.elements),
      });
    }
  }, [existingForm, form]);

  function handleCloneElement(sourceElement: FormElement, sourceIndex: number) {
    insert(sourceIndex + 1, clonedElement(sourceElement, crypto.randomUUID()));
  }

  function removeElementAt(index: number) {
    // Deleting a trigger cascade-deletes its entire revealed subtree; a plain field has no descendants, so
    // this is a straight removal. writeElements replaces the whole array, so there is no separate remove().
    const remaining = elementsAfterDelete(formElements, index);
    // Clear the selection if the selected element was removed — the deleted card itself OR any descendant
    // swept out with it — so the sidebar doesn't keep a dangling id for a field that no longer exists.
    if (selectedElementId !== null && !remaining.some((element) => element.id === selectedElementId)) {
      setSelectedElementId(null);
    }
    writeElements(remaining);
  }

  function handleDeleteElement(index: number) {
    // A cascade delete that sweeps out revealed questions is destructive and has no undo, so confirm it
    // first; a plain-field delete (nothing revealed) removes exactly one element and runs straight through.
    const revealedCount = formElements.length - elementsAfterDelete(formElements, index).length - 1;
    if (revealedCount > 0) {
      setPendingCascadeDelete({ index, revealedCount });
      return;
    }
    removeElementAt(index);
  }

  function handleUpdateConfig(index: number, updates: Partial<Omit<FormElement, 'kind'>>) {
    const currentElements = [...formElements];
    const previous = currentElements[index];
    // `kind` is excluded from the update type, so only `config` decorrelates
    // here: a Partial's config can't be tied back to this element's kind.
    const updated = { ...previous, ...updates } as FormElement;
    currentElements[index] = updated;

    // Conditional-logic rules match this Dropdown trigger's values by display text, so deleting a static
    // option must scrub that text from the rules on THIS trigger — otherwise the rule dangles on a value
    // no admin can pick. Scoped to `updated.id` so a same-text option on a different Dropdown is untouched.
    // The rule itself is kept (even if left with no values) so the revealed field and the admin's intent
    // survive; save-time validation then prompts for a new value. Only when conditional logic is live for
    // this form — otherwise there are no rules to keep consistent and we must not mutate logicRules.
    const deletedOptionValues = conditionalLogicEnabled ? deletedStaticOptionValues(previous, updated) : [];
    const reconciled = deletedOptionValues.reduce((elements, value) => removeValueFromRules(elements, updated.id, value), currentElements);

    setValue('elements', reconciled, { shouldDirty: true });
  }

  // Revealed ids for a rule, in flat-array order — the reconciler's dependent set to add to / remove from.
  function revealedIdsForRule(elements: FormElement[], ruleId: string): string[] {
    return elements.filter((el) => el.dependsOn?.logicRuleId === ruleId).map((el) => el.id);
  }

  function writeElements(next: FormElement[]) {
    setValue('elements', next, { shouldDirty: true });
  }

  // Move `elementId` (already reconciled to belong to `ruleId`) to the flat-array position for box slot
  // `boxSlot`. Remove-then-splice so the slot math is computed against the array without the moved element.
  function positionInReveal(elements: FormElement[], elementId: string, ruleId: string, triggerId: string, boxSlot: number): FormElement[] {
    const moved = elements.find((el) => el.id === elementId);
    if (!moved) return elements;
    const without = elements.filter((el) => el.id !== elementId);
    const insertAt = boxSlotToFlatInsertIndex(without, ruleId, boxSlot, triggerId);
    without.splice(insertAt, 0, moved);
    return without;
  }

  function handleDrop(drop: DesignerDrop) {
    // Selection is held by id and its index is derived from that id, so it follows its element across any
    // mutation a drop makes — no manual re-resolve is needed after writing the rebuilt array.
    const commit = (next: FormElement[]) => writeElements(next);

    switch (drop.kind) {
      case 'create': {
        const newElement = buildDroppedElement(drop.label);
        if (!newElement) return;
        // targetIndex is a FLAT-CANVAS index (revealed fields excluded); translate to the full-array index
        // so a new field lands in canvas order and keeps each trigger + its revealed block contiguous — the
        // fill-time renderer groups by array position, so a raw insert would scatter revealed fields.
        insert(flatCanvasIndexToArrayIndex(formElements, revealedIds, drop.targetIndex), newElement);
        return;
      }
      case 'reorder': {
        // fromIndex/toIndex are FLAT-CANVAS indices (revealed children excluded). A dragged trigger must
        // carry its revealed children as a block, and both indices must translate to full-array positions —
        // otherwise children strand at their old slots and the box renders empty after the move.
        const draggedId = orderedCardIds[drop.fromIndex];
        if (draggedId === undefined) return;
        const draggedEl = formElements.find((el) => el.id === draggedId);
        if (!draggedEl) return;
        // The moved block = the dragged element + any fields revealed by its rules (they render in its box).
        const ownRuleIds = new Set((draggedEl.logicRules ?? []).map((rule) => rule.id));
        const blockIds = new Set<string>([
          draggedId,
          ...formElements.filter((el) => el.dependsOn && ownRuleIds.has(el.dependsOn.logicRuleId)).map((el) => el.id),
        ]);
        const block = formElements.filter((el) => blockIds.has(el.id));
        const without = formElements.filter((el) => !blockIds.has(el.id));
        const revealedAfter = new Set(without.filter((el) => el.dependsOn).map((el) => el.id));
        const insertAt = flatCanvasIndexToArrayIndex(without, revealedAfter, drop.toIndex);
        without.splice(insertAt, 0, ...block);
        commit(without);
        return;
      }
      case 'create-in-reveal': {
        const newElement = buildDroppedElement(drop.label);
        if (!newElement) return;
        const trigger = formElements.find((el) => el.id === drop.triggerId);
        // Reconcile the rule the box belongs to (drop.ruleId) — NOT the trigger's first rule. On a trigger with
        // several rules the box may be a later one; reading [0] would overwrite its condition with rule 0's.
        // A brand-new draft box has no persisted rule yet, so the ?? fallbacks apply.
        const targetRule = trigger?.logicRules?.find((rule) => rule.id === drop.ruleId);
        const reconciled = reconcileShowFieldRule([...formElements, newElement], {
          triggerId: drop.triggerId,
          ruleId: drop.ruleId,
          operator: targetRule?.operator ?? 'is',
          values: targetRule?.values ?? [],
          dependentIds: [...revealedIdsForRule(formElements, drop.ruleId), newElement.id],
        });
        commit(positionInReveal(reconciled, newElement.id, drop.ruleId, drop.triggerId, drop.toIndex));
        return;
      }
      case 'move-to-reveal': {
        if (drop.id === drop.triggerId) return; // a trigger can't reveal itself
        const trigger = formElements.find((el) => el.id === drop.triggerId);
        // Re-point the moved card's own dependsOn onto the new box. Its descendants keep pointing at the moved
        // card, so they follow it — only this one back-pointer changes. Read the box's own rule (drop.ruleId),
        // not the trigger's first rule, or a move into a later rule's box would clobber its condition.
        const targetRule = trigger?.logicRules?.find((rule) => rule.id === drop.ruleId);
        const reconciled = reconcileShowFieldRule(formElements, {
          triggerId: drop.triggerId,
          ruleId: drop.ruleId,
          operator: targetRule?.operator ?? 'is',
          values: targetRule?.values ?? [],
          dependentIds: [...revealedIdsForRule(formElements, drop.ruleId).filter((id) => id !== drop.id), drop.id],
        });
        // A rule-carrying card carries its revealed children, so move the whole block into the box contiguously
        // (same block shape as 'reorder'). A plain field is a one-element block. orderElementsByTrigger
        // regroups any deeper descendants on save; a contiguous one-level block keeps the box valid in-flight.
        const movedEl = reconciled.find((el) => el.id === drop.id);
        if (!movedEl) return;
        const movedRuleIds = new Set((movedEl.logicRules ?? []).map((rule) => rule.id));
        const blockIds = new Set<string>([
          drop.id,
          ...reconciled.filter((el) => el.dependsOn && movedRuleIds.has(el.dependsOn.logicRuleId)).map((el) => el.id),
        ]);
        const block = reconciled.filter((el) => blockIds.has(el.id));
        const without = reconciled.filter((el) => !blockIds.has(el.id));
        const insertAt = boxSlotToFlatInsertIndex(without, drop.ruleId, drop.toIndex, drop.triggerId);
        without.splice(insertAt, 0, ...block);
        commit(without);
        return;
      }
      case 'move-to-canvas': {
        // Drag a revealed field back out: strip its dependsOn, then drop it at the canvas position released on.
        const moved = formElements.find((el) => el.id === drop.id);
        const owningRuleId = moved?.dependsOn?.logicRuleId;
        const owningTriggerId = moved?.dependsOn?.fieldId;
        if (!owningRuleId || !owningTriggerId) return;
        const trigger = formElements.find((el) => el.id === owningTriggerId);
        // Reconcile the rule the dragged field actually belonged to, not the trigger's first rule — otherwise
        // dragging a field out of a later rule's box would rewrite that rule's condition with rule 0's.
        const owningRule = trigger?.logicRules?.find((rule) => rule.id === owningRuleId);
        const reconciled = reconcileShowFieldRule(formElements, {
          triggerId: owningTriggerId,
          ruleId: owningRuleId,
          operator: owningRule?.operator ?? 'is',
          values: owningRule?.values ?? [],
          dependentIds: revealedIdsForRule(formElements, owningRuleId).filter((id) => id !== drop.id),
        });
        // The element is no longer revealed; place it at the flat-canvas slot (revealed cards excluded).
        const without = reconciled.filter((el) => el.id !== drop.id);
        const movedEl = reconciled.find((el) => el.id === drop.id);
        if (!movedEl) return;
        const revealedAfter = new Set(without.filter((el) => el.dependsOn).map((el) => el.id));
        const insertAt = flatCanvasIndexToArrayIndex(without, revealedAfter, drop.toIndex);
        without.splice(insertAt, 0, movedEl);
        commit(without);
        return;
      }
      case 'reorder-in-reveal': {
        // Reorder within a box: box slot N maps to the Nth array element revealed by this rule. This holds
        // because the box renders members in array order and orderElementsByTrigger keeps a rule's revealed
        // members contiguous — if they ever become non-contiguous, this slot→index mapping breaks. toIndex can
        // be the append slot (members.length) when the drop lands on the box gutter — the helper clamps it.
        const flat = revealReorderFlatIndices(formElements, drop.ruleId, drop.fromIndex, drop.toIndex);
        if (!flat) return;
        move(flat.fromFlat, flat.toFlat);
        return;
      }
    }
  }

  // Selection is held by the element's own saved id (the same id the cards and DnD use) and the index derived
  // from it, so it follows its element across any reorder/insert/delete and survives config edits.
  const selectedElementIndex = useMemo(() => fields.findIndex((field) => field.id === selectedElementId), [fields, selectedElementId]);
  const selectedElement = selectedElementIndex === -1 ? null : formElements[selectedElementIndex];

  function handleContainerClick() {
    setSelectedElementId(null);
  }

  function handleSubmission(values: FormDesignerForm) {
    const validation = validateForm(values.type, values.elements);

    // Surface incomplete conditional-logic rules inline (red value control + dropzone) alongside the toast, and
    // snapshot the rules that exist at THIS save attempt + the conflicting ones so each offending row is flagged.
    // An empty draft rule isn't in logicRules yet (the reconciler drops a rule with no values + no dependents),
    // so a rule added after a failed save stays clean until the next save.
    const blockSave = (message: string, conflictingRuleIds: Set<string> = new Set()) => {
      setLogicErrors({
        ruleIdsAtSave: new Set(values.elements.flatMap((element) => (element.logicRules ?? []).map((rule) => rule.id))),
        conflictingRuleIds,
      });
      toast.error(message);
    };

    if (!validation.isValid) {
      blockSave(validation.error, validation.conflictingRuleIds);
      return;
    }
    // A rule pointing at a deleted list value can't be verified from the pure validateForm — block here instead.
    // Three list states, three outcomes: still loading → wait and retry; list missing/fetch failed → can't verify
    // (editing the rule is the fix, not retrying); loaded but value gone → the rule is stale.
    if (logicValuesResolving) {
      blockSave(LogicRuleConfigLabels.valuesStillLoading);
      return;
    }
    if (hasUnverifiableList) {
      blockSave(LogicRuleConfigLabels.valuesUnverifiable);
      return;
    }
    if (hasDeletedValues) {
      blockSave(LogicRuleConfigLabels.deletedValueMessage);
      return;
    }
    setLogicErrors(null);

    const baseFormData = {
      name: values.name,
      // Normalise so each trigger is immediately followed by its revealed fields — the fill-time renderer
      // groups by array position, so this keeps the saved layout correct however the fields were dragged.
      elements: orderElementsByTrigger(values.elements),
    };

    const shouldNavigateBack = openSaveChanges;

    if (formId) {
      const updateData: UpdateFormRequestDTO = {
        ...baseFormData,
        formId,
      };
      updateFormMutation.mutate(updateData, {
        onSuccess: () => {
          toast.success('Form updated successfully');
          form.reset(form.getValues());
          if (shouldNavigateBack) {
            navigate(-1);
          }
        },
      });
    } else {
      const createData: CreateFormRequestDTO = {
        ...baseFormData,
        type: values.type,
      };
      createFormMutation.mutate(createData, {
        onSuccess: (data) => {
          toast.success('Form created successfully');
          if (shouldNavigateBack) {
            navigate(-1);
          } else {
            navigate(`/forms/${data.form.id}`);
          }
        },
      });
    }
  }

  function handleSave() {
    form.handleSubmit(handleSubmission)();
    setOpenSaveChanges(false);
  }

  function handleDiscard() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  }

  if (formId && !existingForm) {
    return (
      <div className="fixed inset-0 z-30 flex h-screen w-full flex-col items-center justify-center bg-white">
        <div className="text-lg">Loading form...</div>
      </div>
    );
  }

  // Defensive guard: the useEffect above redirects Members on mount, but
  // returning null here keeps the editor from rendering the frame before
  // the redirect lands.
  if (!isAdmin) return null;

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmission)} className="fixed inset-0 z-30 flex h-screen w-full flex-col bg-white">
          <FormTopbar />
          {!isPreviewOpen ? (
            <FormDesignerDndProvider onDrop={handleDrop} orderedCardIds={orderedCardIds} revealDropGuard={revealDropGuard}>
              <div className="flex min-h-0 flex-1 grid-cols-12">
                <div className="border-grey-3 flex w-[260px] flex-col border-r-2">
                  <div className="flex flex-col">
                    <span className="text-default-text px-2 pt-2 text-lg font-medium">Fields</span>
                    <span className="text-secondary-text px-2 pb-2 text-xs">
                      Drag and drop fields into the body of the form, and use the grips to rearrange
                    </span>
                  </div>
                  <ScrollArea className="min-h-0 flex-1" showShadow>
                    {/* Toolbox is a drag source only — each item is individually draggable, so the list needs no drop target. */}
                    <div className="mr-2 flex flex-col px-2 pb-2">
                      {Object.entries(
                        FormDesignerElements.filter((el) => {
                          // Hide IssueSelect from RepairForm (issues are linked via issueId, not form elements)
                          if (shouldHideIssueSelect(el.kind, formType)) {
                            return false;
                          }
                          return true;
                        }).reduce(
                          (acc, element) => {
                            const category = element.category;
                            if (!acc[category]) {
                              acc[category] = [];
                            }
                            acc[category].push(element);
                            return acc;
                          },
                          {} as Record<string, typeof FormDesignerElements>,
                        ),
                      ).map(([category, elements]) => (
                        <div key={category} className="flex flex-col py-2">
                          <span className="text-default-text text-sm">{category}</span>
                          {elements.map((element) => (
                            <DraggableFormDesignerElementListItem key={element.label} element={element} usedElements={usedElements} formType={formType} />
                          ))}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                <div className="flex min-h-0 flex-1 flex-col">
                  <CanvasDropZone onContainerClick={handleContainerClick}>
                    {fields.map((field, index) => {
                      // Hide Issue Select elements from RepairForms when new issues architecture is enabled
                      if (formElements[index] && shouldHideIssueSelect(formElements[index].kind, formType)) {
                        return null;
                      }
                      // Revealed fields render inside their trigger's reveal box, not in the flat canvas.
                      if (revealedIds.has(formElements[index]?.id)) {
                        return null;
                      }
                      return (
                        <FormDesignerElementItem
                          key={field.id}
                          formElement={formElements[index]}
                          allElements={formElements}
                          onElementsChange={writeElements}
                          conditionalLogicEnabled={conditionalLogicEnabled}
                          logicOpen={openLogicElementIds.has(formElements[index].id) || (formElements[index].logicRules?.length ?? 0) > 0}
                          onLogicOpenChange={(open) => setLogicOpen(formElements[index].id, open)}
                          logicErrors={logicErrors}
                          flaggedRuleIds={flaggedRuleIds}
                          cloneElement={() => handleCloneElement(formElements[index], index)}
                          deleteElement={() => handleDeleteElement(index)}
                          id={formElements[index].id}
                          index={index}
                          isSelected={selectedElementId === field.id}
                          onSelect={() => setSelectedElementId(field.id)}
                          onDeselect={() => setSelectedElementId(null)}
                          cloneElementAt={handleCloneElement}
                          deleteElementAt={handleDeleteElement}
                          selectElementAt={(i) => setSelectedElementId(formElements[i]?.id ?? null)}
                          selectedElementId={selectedElement?.id ?? null}
                          hiddenCanvasIds={hiddenCanvasIds}
                          isLogicOpen={(elementId) => openLogicElementIds.has(elementId)}
                          onChildLogicOpenChange={setLogicOpen}
                        />
                      );
                    })}
                  </CanvasDropZone>
                </div>
                {/* Radix wraps content in a shrink-to-fit `display: table` div; force block so long editor text wraps to the 360px panel instead of overflowing. */}
                <ScrollArea className="border-grey-3 min-h-0 w-[360px] border-l" viewportClassName="[&>div]:!block">
                  <ConfiguratorPanel
                    key={selectedElementId}
                    formElement={selectedElement}
                    onUpdateConfig={(_, updates) => selectedElementIndex !== -1 && handleUpdateConfig(selectedElementIndex, updates)}
                  />
                </ScrollArea>
              </div>
            </FormDesignerDndProvider>
          ) : (
            <ScrollArea className="min-h-0 flex-1">
              <FormDesignerPreview form={form.getValues()} />
            </ScrollArea>
          )}

          <FormDesignerJSONPreview open={openJSONPreview} setOpen={setOpenJSONPreview} />
        </form>
        <SaveChangesDialog
          open={openSaveChanges}
          onOpenChange={setOpenSaveChanges}
          saveCallback={handleSave}
          discardCallback={handleDiscard}
          loading={createFormMutation.isPending || updateFormMutation.isPending}
          title="Save changes?"
          description="Do you want to save your changes before leaving?"
        />
        <ConfirmDialog
          open={pendingCascadeDelete !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingCascadeDelete(null);
            }
          }}
          title="Delete this question and its revealed fields?"
          description={
            pendingCascadeDelete
              ? `This also deletes ${pendingCascadeDelete.revealedCount} revealed ${pendingCascadeDelete.revealedCount === 1 ? 'question' : 'questions'} nested under it. This can't be undone.`
              : undefined
          }
          confirmLabel="Delete"
          confirmVariant="destructive"
          confirmIcon={<DeleteIcon className="text-white" />}
          onConfirm={() => {
            if (pendingCascadeDelete) {
              removeElementAt(pendingCascadeDelete.index);
              setPendingCascadeDelete(null);
            }
          }}
        />
        <Sheet open={isActivityOpen} onOpenChange={setIsActivityOpen}>
          <SheetContent>
            <SheetTitle className="sr-only">Activity</SheetTitle>
            <FormRevisionHistory form={existingForm?.form} />
          </SheetContent>
        </Sheet>
      </FormProvider>
    </>
  );
}

function FormDesignerPreview({ form }: { form: FormDesignerForm }) {
  const { isAWA } = useIsAWA();
  const toast = useToast();

  const { data: assets = [] } = useQuery(getAssetsQueryOptions({ limit: 100, isAWA, filters: { state: RecordState.Active } }));
  const [selectedAsset, setSelectedAsset] = useState<AssetDTO | null>(null);

  // Automatically select the first asset when assets are loaded
  useEffect(() => {
    if (assets.length > 0 && !selectedAsset) {
      setSelectedAsset(assets[0]);
    }
  }, [assets, selectedAsset]);

  const formInstance = useForm<FormRendererData>({
    defaultValues: {
      ...form.elements.reduce(
        (acc, element) => ({
          ...acc,
          ...(element.kind === FormElementKind.Table
            ? {
                [element.id]: [[...initializeTableInitialValues(element as TableFormElement)]],
              }
            : {
                [element.id]: undefined,
              }),
        }),
        {} as FormRendererData,
      ),
    },
  });

  const handleSubmit = () => {
    toast.success('Form submitted successfully');
  };

  return (
    <div className="bg-grey-1 flex h-full w-full flex-col p-[14px]">
      <div className="bg-default-light-blue text-default-blue mx-auto mt-[10px] mb-4 flex justify-center rounded-md p-[14px] text-sm">
        <InformationIcon className="mr-1" />
        <span>
          This is a <strong>preview only</strong>. Toggle back to 'Edit' to continue making changes.
        </span>
      </div>
      <div className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 mx-auto w-full max-w-[1050px] overflow-y-auto rounded-md bg-white p-[30px] shadow">
        {assets.length === 0 ? (
          <div className="text-grey-5 flex h-32 items-center justify-center">No assets available. Please create an asset first.</div>
        ) : (
          <FormProvider {...formInstance}>
            <form onSubmit={formInstance.handleSubmit(handleSubmit)}>
              {selectedAsset && (
                <div className="flex flex-col gap-4">
                  <FormElementsRenderer
                    asset={selectedAsset}
                    elements={form.elements}
                    readonly={true}
                    isPreview={true}
                    form={
                      {
                        type: form.type,
                      } as FormDTO
                    }
                  />
                </div>
              )}
            </form>
          </FormProvider>
        )}
      </div>
    </div>
  );
}

function FormDesignerElementItem({
  formElement,
  allElements,
  onElementsChange,
  conditionalLogicEnabled,
  logicOpen = false,
  onLogicOpenChange,
  logicErrors = null,
  flaggedRuleIds = EMPTY_RULE_IDS,
  cloneElement,
  deleteElement,
  id,
  index,
  isSelected,
  onSelect,
  onDeselect,
  revealGroup,
  boxMemberIds,
  hiddenCanvasIds,
  suppressLogic,
  cloneElementAt,
  deleteElementAt,
  selectElementAt,
  selectedElementId,
  isLogicOpen,
  onChildLogicOpenChange,
}: {
  formElement: FormElement;
  allElements: FormElement[];
  onElementsChange: (next: FormElement[]) => void;
  conditionalLogicEnabled: boolean;
  // Open-state is owned by the parent (keyed by element id) so it persists when a card moves between the flat
  // canvas and a reveal box — the move unmounts the old instance and mounts a new one, which would drop any
  // open-state held in the card's own useState.
  logicOpen?: boolean;
  onLogicOpenChange?: (open: boolean) => void;
  // Null until a save attempt fails; non-null turns on the inline red flags. `ruleIdsAtSave` are the rules
  // eligible for the dropzone "no fields to show" flag (those that existed at the last failed save).
  logicErrors?: { ruleIdsAtSave: ReadonlySet<string>; conflictingRuleIds: ReadonlySet<string> } | null;
  // The rules whose VALUE control is invalid on a save attempt (incomplete, conflicting, or deleted-value).
  flaggedRuleIds?: ReadonlySet<string>;
  cloneElement: (sourceElement: FormElement, sourceIndex: number) => void;
  deleteElement: (uniqueId: string) => void;
  id: string;
  index: number;
  isSelected?: boolean;
  onSelect?: () => void;
  // Clears selection when focus leaves the card entirely, so Tab doesn't leave a trail of highlighted cards.
  onDeselect?: () => void;
  // Set when this card is rendered INSIDE a reveal box (its DnD id carries the group; logic UI is off).
  revealGroup?: string;
  // The box's ordered member ids, set only for a reveal-box member — gives the drag probe box order.
  boxMemberIds?: string[];
  // Flat-canvas cards the canvas hides (an IssueSelect on a RepairForm). The keyboard reorder steps over them.
  hiddenCanvasIds?: ReadonlySet<string>;
  suppressLogic?: boolean;
  // Flat-index handlers used to scope nested reveal-box children to their OWN element (not the trigger).
  cloneElementAt?: (sourceElement: FormElement, sourceIndex: number) => void;
  deleteElementAt?: (index: number) => void;
  selectElementAt?: (index: number) => void;
  selectedElementId?: string | null;
  // Reads/writes the parent's per-element open-set for nested cards, so a revealed field's own logic panel
  // persists across a move between boxes exactly like a top-level card's does.
  isLogicOpen?: (elementId: string) => boolean;
  onChildLogicOpenChange?: (elementId: string, open: boolean) => void;
}) {
  const { mockElement: MockElement, isUnique, isNotCloneable, icon } = definitionFor(formElement.kind);

  const supportsLogic = conditionalLogicEnabled && !suppressLogic && formElement.kind === FormElementKind.Dropdown;
  // A revealed field may open its own rule only while it stays within the nesting cap (unbounded today).
  const canOpenLogic = supportsLogic && (revealGroup === undefined || canNestUnder(allElements, id));

  // A reveal child's arrow-key reorder resolves against its box's member slots. A box member already receives
  // those ordered ids as `boxMemberIds` (the ids its box actually renders) — reuse them rather than re-parsing
  // `revealGroup` and re-filtering `allElements`, so the keyboard path and the drop path can't disagree.
  const revealMemberIds = boxMemberIds ?? [];

  // Pointer reorder uses native HTML5 drag (no keyboard path); ArrowUp/ArrowDown on a focused card fires the
  // same `onDrop` reorder seam, then announces the new position for screen readers (Pragmatic's live region).
  const { onDrop, orderedIdsRef } = useFormDesignerDndStable();
  const move = (direction: MoveDirection) => {
    const drop = resolveReorderMove({ id, direction, revealGroup, orderedCanvasIds: orderedIdsRef.current, revealMemberIds, hiddenCanvasIds });
    if (!drop) return;
    onDrop(drop);
    // The announced position counts only what the user can see: box members in a reveal box, or the visible
    // (hidden-excluded) canvas cards on the flat canvas — so it never claims a slot the pointer path can't reach.
    const visibleCanvasCount = hiddenCanvasIds?.size
      ? orderedIdsRef.current.filter((cardId) => !hiddenCanvasIds.has(cardId)).length
      : orderedIdsRef.current.length;
    const total = revealGroup ? revealMemberIds.length : visibleCanvasCount;
    const announcedPosition = revealGroup
      ? drop.toIndex + 1
      : orderedIdsRef.current.slice(0, drop.toIndex + 1).filter((cardId) => !hiddenCanvasIds?.has(cardId)).length;
    announce(`Moved ${formElement.label} to position ${announcedPosition} of ${total}`);
    // The reorder re-renders (and can remount) the moved card, which may not have committed in the first frame.
    // Retry a few frames until the card is in the DOM so keyboard focus is never silently dropped to <body>.
    let attempts = 0;
    const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    const restoreFocus = () => {
      const card = document.querySelector<HTMLElement>(`[data-field-card-id="${escapedId}"]`);
      if (!card) {
        if (attempts++ < 5) requestAnimationFrame(restoreFocus);
        return;
      }
      // Keep the moved card in view: a reorder doesn't scroll on its own, so a card pushed past the fold would
      // move out of sight. `block: 'nearest'` scrolls only when it isn't already fully visible.
      card.focus({ preventScroll: true });
      card.scrollIntoView({ block: 'nearest' });
    };
    requestAnimationFrame(restoreFocus);
  };
  const handleReorderKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return; // ignore arrows typed inside nested inputs / child cards
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      move('up');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      move('down');
    }
  };

  const persistedRules = formElement.logicRules ?? [];
  const persistedRuleIds = persistedRules.map((rule) => rule.id);
  // Which rule rows are OPEN, by stable id — held in state so a row survives even when its rule is absent from
  // `logicRules` (an empty draft, or a rule whose value was cleared). A rule only enters `logicRules` once it
  // has a value or a dependent, so without this a cleared/empty row would vanish. The trash icon is the only
  // thing that closes a row. Seeded lazily from the persisted rules so a reload shows saved rules.
  const [openRuleIds, setOpenRuleIds] = useState<string[]>(() => persistedRuleIds);
  const addRuleRow = () => setOpenRuleIds((prev) => [...prev, crypto.randomUUID()]);
  const closeRuleRow = (closedId: string) => setOpenRuleIds((prev) => prev.filter((openId) => openId !== closedId));
  // Clone a rule: deep-copy its revealed subtree into a new rule with the SAME operator but NO values (so the
  // clone starts value-less and doesn't overlap the source — the admin picks fresh values). The cloned elements
  // are spliced in; attachLogicRules wires the new rule to the top-level clones.
  const cloneRuleRow = (sourceRuleId: string) => {
    const sourceRule = persistedRules.find((rule) => rule.id === sourceRuleId);
    const { newRuleId, clonedElements } = cloneRuleSubtree(allElements, id, sourceRuleId);
    const topLevelCloneIds = clonedElements.filter((el) => el.dependsOn?.logicRuleId === newRuleId).map((el) => el.id);
    const next = reconcileShowFieldRule([...allElements, ...clonedElements], {
      triggerId: id,
      ruleId: newRuleId,
      operator: sourceRule?.operator ?? 'is',
      values: [],
      dependentIds: topLevelCloneIds,
    });
    onElementsChange(next);
    setOpenRuleIds((prev) => [...prev, newRuleId]);
  };
  // Render every open row plus any persisted rule not yet in the open set (e.g. just-loaded, or added by a
  // clone). A persisted id that reappears is de-duped.
  const ruleIds = Array.from(new Set([...openRuleIds, ...persistedRuleIds]));

  const revealedChildrenForRule = (forRuleId: string): FormElement[] =>
    supportsLogic ? allElements.filter((el) => el.dependsOn?.logicRuleId === forRuleId) : [];
  // On a save attempt, flag an open rule whose reveal box holds no fields yet.
  const ruleHasNoDependents = (forRuleId: string): boolean => revealedChildrenForRule(forRuleId).length === 0;
  // Incompleteness is read LIVE from the current rule (not the failed-save snapshot) so the red clears the moment
  // the admin picks a value — conflict/deleted flags stay snapshot-based, this one tracks the edit.
  const ruleHasNoValues = (forRuleId: string): boolean => (persistedRules.find((rule) => rule.id === forRuleId)?.values.length ?? 0) === 0;

  const showLogicErrors = logicErrors !== null;
  const erroredRuleIds = logicErrors?.ruleIdsAtSave ?? EMPTY_RULE_IDS;

  // The drag preview is display-only and shows just the FIRST rule's row + box — enough to signal "this is a
  // logic trigger" without re-rendering every rule under the cursor. A multi-rule trigger's preview is thus a
  // deliberate single-rule snapshot, not a full replica of the resting card.
  const previewRuleId = ruleIds[0];
  const revealedChildren = revealedChildrenForRule(previewRuleId);

  // The floating overlay shown while dragging (native HTML5 preview can't animate). When the logic block is
  // open it carries a DISPLAY-ONLY replica of the first rule's row + reveal box (never the live
  // RevealDropZone/builder — those register a drop target + run queries).
  const previewRule = persistedRules.find((rule) => rule.id === previewRuleId);
  const previewTriggerCard = (
    <div className="border-grey-3 grid grid-cols-[auto_auto_1fr] items-center gap-x-2 gap-y-1 rounded-md border bg-white p-4">
      <GripDotVerticalIcon className="text-secondary-text" />
      {icon}
      <Label required={formElement.required} className="min-w-0 truncate">
        {formElement.label}
      </Label>
      <div className="col-span-2 col-start-3 min-w-0 overflow-hidden">
        <MockElement element={formElement} />
      </div>
    </div>
  );
  const dragPreview =
    supportsLogic && logicOpen ? (
      // Mirrors the resting open card: trigger card on the plain bg, then a grey dashed panel grouping the rule
      // row + dashed reveal box below it.
      <div className="flex flex-col gap-3">
        <div className="shadow-dark-sm rounded-md">{previewTriggerCard}</div>
        <div className="bg-grey-1 flex flex-col gap-3 rounded-md p-5">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-default-text shrink-0">{LogicRuleConfigLabels.ifValue}</span>
            <span className="text-secondary-text shrink-0">
              {previewRule?.operator === 'is not' ? LogicRuleConfigLabels.operatorIsNot : LogicRuleConfigLabels.operatorIs}
            </span>
            <div className="flex min-w-0 flex-wrap gap-1">
              {(previewRule?.values ?? []).map((value) => (
                <span key={value} className="bg-grey-2 rounded-full px-2 py-0.5 text-xs">
                  {value}
                </span>
              ))}
            </div>
            <span className="text-default-text shrink-0">{LogicRuleConfigLabels.then}</span>
            <span className="text-secondary-text shrink-0">{LogicRuleConfigLabels.showFields}</span>
          </div>
          <div className="bg-grey-1 rounded-10 border-grey-5 flex min-h-24 flex-col gap-2 border-2 border-dashed p-5">
            {revealedChildren.length === 0 ? (
              <span className="text-secondary-text text-center text-sm">{LogicRuleConfigLabels.revealBoxHint}</span>
            ) : (
              revealedChildren.map((child) => {
                const { mockElement: ChildMock, icon: childIcon } = definitionFor(child.kind);
                return (
                  <div key={child.id} className="border-grey-3 grid grid-cols-[auto_auto_1fr] items-center gap-x-2 gap-y-1 rounded-md border bg-white p-4">
                    <GripDotVerticalIcon className="text-secondary-text" />
                    {childIcon}
                    <Label required={child.required} className="min-w-0 truncate">
                      {child.label}
                    </Label>
                    <div className="col-span-2 col-start-3 min-w-0 overflow-hidden">
                      <ChildMock element={child} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    ) : (
      <div className="shadow-dark-sm rounded-md">{previewTriggerCard}</div>
    );

  return (
    <CanvasDraggableCard
      id={id}
      preview={dragPreview}
      revealGroup={revealGroup}
      boxMemberIds={boxMemberIds}
      isTrigger={(formElement.logicRules?.length ?? 0) > 0}>
      {({ rootProps, innerStyle, gripRef }) => {
        const triggerCard = (style?: React.CSSProperties) => (
          <div
            style={style}
            tabIndex={0}
            data-field-card-id={id}
            onKeyDown={handleReorderKey}
            // Selection follows focus: focusing the card (Tab or click) selects it, so exactly one field is ever
            // highlighted and the settings panel tracks it. Guarded so focusing a nested button doesn't re-select.
            onFocus={(e) => {
              if (e.target === e.currentTarget) onSelect?.();
            }}
            // Clear the highlight only when focus leaves to nothing (Tab past the canvas, or a click on empty
            // space → relatedTarget is null), so a card doesn't stay highlighted after focus has left the editor.
            // When focus moves to another card or control, that target drives its own selection, so we must NOT
            // clear here — doing so would fight click-to-select on a sibling card's action button.
            onBlur={(e) => {
              if (e.relatedTarget) return;
              onDeselect?.();
            }}
            aria-label={`${formElement.label} field`}
            aria-keyshortcuts="ArrowUp ArrowDown"
            // Keep an explicit focus-visible ring rather than relying on "focus selects → selected shows a ring":
            // if focus and selection ever diverge (e.g. selection cleared while focus stays on the card) a keyboard
            // user would otherwise lose every visible focus cue (WCAG 2.4.7). The browser outline is still removed
            // so the two rings don't double up, but the ring guarantees focus is always visible.
            className={cn(
              'hover:shadow-dark-sm group focus-visible:ring-default-blue relative grid cursor-pointer grid-cols-[auto_auto_1fr_auto] items-center gap-x-2 gap-y-1 rounded-md border bg-white p-4 transition duration-300 ease-out focus-visible:ring-2 focus-visible:outline-none',
              isSelected ? 'border-default-blue ring-default-blue ring-1' : 'border-grey-3',
            )}
            onClick={onSelect}>
            <div ref={gripRef} aria-label="Drag to reorder" className="cursor-grab active:cursor-grabbing">
              <GripDotVerticalIcon className="text-secondary-text" />
            </div>
            {icon}
            <Label required={formElement.required} className="min-w-0 truncate">
              {formElement.label}
            </Label>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
              {canOpenLogic && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={LogicRuleConfigLabels.addLogic}
                  className="bg-grey-1 text-default-text h-auto gap-1 p-1 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Stays visible once open: first click opens the panel, each later click adds another rule.
                    onLogicOpenChange?.(true);
                    addRuleRow();
                  }}>
                  <LogicIcon />
                  {LogicRuleConfigLabels.addLogic}
                </Button>
              )}
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Clone element"
                      className="bg-grey-1 h-auto w-auto p-1"
                      disabled={isUnique || isNotCloneable}
                      onClick={(e) => {
                        e.stopPropagation();
                        cloneElement(formElement, index);
                      }}>
                      <CloneIcon className={isUnique || isNotCloneable ? 'opacity-30' : ''} />
                    </Button>
                  </TooltipTrigger>
                  {(isUnique || isNotCloneable) && (
                    <TooltipPortal>
                      <TooltipContent side="top" sideOffset={0} className="bg-default-text pointer-events-none text-[10px] text-white">
                        <TooltipArrow className="fill-default-text" />
                        {isUnique && 'This widget can only be used once per form'}
                        {isNotCloneable && 'This widget cannot be cloned'}
                      </TooltipContent>
                    </TooltipPortal>
                  )}
                </Tooltip>
              </TooltipProvider>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Delete element"
                className="bg-grey-1 h-auto w-auto p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteElement(id);
                }}>
                <DeleteIcon className="text-default-red" />
              </Button>
            </div>
            <div className="col-span-2 col-start-3 min-w-0 overflow-hidden">
              <MockElement element={formElement} />
            </div>
          </div>
        );

        // Closed / non-logic / past the depth cap: the untransformed wrapper is the drop target; the trigger
        // card carries the slide-apart transform via innerStyle, exactly like every other field card.
        if (!(canOpenLogic && logicOpen)) {
          return (
            <div {...rootProps} className="pb-2">
              {triggerCard(innerStyle)}
            </div>
          );
        }

        // Open: the draggable root is a transparent wrapper so the trigger card stays a full-width card on the
        // plain canvas (inline with other fields), with a grey panel BELOW it grouping the logic. Each rule
        // renders its own row + dashed reveal box, stacked vertically in the grey panel.
        return (
          <div {...rootProps} className="pb-2">
            {/* Inner div carries the slide-apart transform; the untransformed wrapper stays the drop target. */}
            <div style={innerStyle} className="relative flex flex-col gap-3">
              {triggerCard()}
              <div className="bg-grey-1 flex flex-col gap-3 rounded-md p-5" onClick={(e) => e.stopPropagation()}>
                {ruleIds.map((perRuleId) => {
                  const ruleChildren = revealedChildrenForRule(perRuleId);
                  const ruleMemberIds = ruleChildren.map((child) => child.id);
                  return (
                    <div key={perRuleId} className="flex flex-col gap-3">
                      <DropdownLogicRuleBuilder
                        element={formElement as DropdownFormElement}
                        ruleId={perRuleId}
                        allElements={allElements}
                        onElementsChange={onElementsChange}
                        onClone={() => cloneRuleRow(perRuleId)}
                        onClose={() => {
                          closeRuleRow(perRuleId);
                          // Closing the last rule collapses the whole panel; closing one of several just drops it.
                          if (ruleIds.length <= 1) onLogicOpenChange?.(false);
                        }}
                        invalidValues={showLogicErrors && (ruleHasNoValues(perRuleId) || flaggedRuleIds.has(perRuleId))}
                      />
                      <RevealDropZone
                        triggerId={id}
                        ruleId={perRuleId}
                        isEmpty={ruleChildren.length === 0}
                        invalid={showLogicErrors && erroredRuleIds.has(perRuleId) && ruleHasNoDependents(perRuleId)}
                        memberIds={ruleMemberIds}
                        hint={LogicRuleConfigLabels.revealBoxHint}>
                        {ruleChildren.map((child) => {
                          // Nested cards must act on THEMSELVES, not the trigger — resolve each child's own flat index.
                          const childFlatIndex = allElements.findIndex((el) => el.id === child.id);
                          return (
                            <FormDesignerElementItem
                              key={child.id}
                              formElement={child}
                              allElements={allElements}
                              onElementsChange={onElementsChange}
                              conditionalLogicEnabled={conditionalLogicEnabled}
                              logicOpen={(isLogicOpen?.(child.id) ?? false) || (child.logicRules?.length ?? 0) > 0}
                              onLogicOpenChange={(open) => onChildLogicOpenChange?.(child.id, open)}
                              logicErrors={logicErrors}
                              flaggedRuleIds={flaggedRuleIds}
                              cloneElement={() => cloneElementAt?.(child, childFlatIndex)}
                              deleteElement={() => deleteElementAt?.(childFlatIndex)}
                              id={child.id}
                              index={childFlatIndex}
                              isSelected={selectedElementId === child.id}
                              onSelect={() => selectElementAt?.(childFlatIndex)}
                              onDeselect={onDeselect}
                              revealGroup={`${id}:${perRuleId}`}
                              boxMemberIds={ruleMemberIds}
                              cloneElementAt={cloneElementAt}
                              deleteElementAt={deleteElementAt}
                              selectElementAt={selectElementAt}
                              selectedElementId={selectedElementId}
                              isLogicOpen={isLogicOpen}
                              onChildLogicOpenChange={onChildLogicOpenChange}
                            />
                          );
                        })}
                      </RevealDropZone>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      }}
    </CanvasDraggableCard>
  );
}

function FormDesignerElementListItem({
  element,
  isDisabled,
  disabledTooltipContent,
}: {
  element: FormDesignerElementDefinition;
  isDisabled?: boolean;
  disabledTooltipContent?: ReactNode;
}) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'border-grey-2 flex items-center gap-1 rounded-md border-2 bg-white px-2 py-[6.5px] text-sm',
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab active:cursor-grabbing',
            )}>
            <GripDotVerticalIcon className="text-secondary-text" />
            <div className="text-default-text flex items-center gap-1">
              {element.icon}
              <span>{element.label}</span>
            </div>
            {element.isDeprecated && (
              <div className="text-default-orange">
                <WarningIcon />
              </div>
            )}
          </div>
        </TooltipTrigger>
        {disabledTooltipContent && (
          <TooltipPortal>
            <TooltipContent side="right" sideOffset={0} className="text-default-text pointer-events-auto max-w-xs text-sm break-words whitespace-normal">
              <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
              {disabledTooltipContent}
            </TooltipContent>
          </TooltipPortal>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

function DraggableFormDesignerElementListItem({
  element,
  usedElements,
  formType,
}: {
  element: FormDesignerElementDefinition;
  usedElements: FormElementKind[];
  formType: number;
}) {
  const { user } = useUserStore();
  const { isDisabled, isDisabledForGlobalAdmin, disabledTooltipContent } = useMemo(() => {
    const isNotAllowed = match(formType)
      .with(FormType.InspectionForm, () => {
        return element.kind === FormElementKind.IssueSelect;
      })
      .with(FormType.RepairForm, () => {
        return shouldHideIssueSelect(element.kind, formType);
      })
      .with(FormType.ScheduleForm, () => {
        return element.kind === FormElementKind.IssueSelect;
      })
      .otherwise(() => {
        return false;
      });

    const isAlreadyUsed = element.isUnique && usedElements.includes(element.kind);
    const isDeprecated = element.isDeprecated;
    const isDisabled = element.isDisabled;

    const disabled = isNotAllowed || isAlreadyUsed || isDeprecated || isDisabled;
    const disabledForGlobalAdmin = isNotAllowed || isAlreadyUsed || isDeprecated;

    let disabledTooltipContent = element.disabledTooltipContent ?? null;
    if (!disabledTooltipContent) {
      if (isNotAllowed) {
        disabledTooltipContent = <span>This field is not allowed for this form type.</span>;
      } else if (user?.roles.includes(PermissionRoles.SitemateSuperUser) ? disabledForGlobalAdmin : isDisabled) {
        disabledTooltipContent = <span>This field is disabled.</span>;
      } else if (isAlreadyUsed) {
        disabledTooltipContent = <span> This field can only be used once per form.</span>;
      } else if (isDeprecated) {
        disabledTooltipContent = <span>This field will be discontinued soon. Prepare for upcoming changes.</span>;
      }
    }

    return { isDisabled: disabled, isDisabledForGlobalAdmin: disabledForGlobalAdmin, disabledTooltipContent: disabledTooltipContent };
  }, [element.isDeprecated, element.disabledTooltipContent, element.kind, element.isDisabled, element.isUnique, formType, usedElements, user?.roles]);

  const dragDisabled = (user?.roles.includes(PermissionRoles.SitemateSuperUser) ? isDisabledForGlobalAdmin : isDisabled) ?? false;

  // The floating overlay shown while dragging a toolbox item onto the canvas — fully opaque (the faded
  // one is the in-place sidebar source, dimmed by the wrapper's opacity below).
  const dragPreview = <FormDesignerElementListItem element={element} isDisabled={dragDisabled} disabledTooltipContent={disabledTooltipContent} />;

  return (
    <ToolboxDraggableItem label={element.label} isDragDisabled={dragDisabled} preview={dragPreview}>
      {({ rootRef, isDragging }) => (
        // While dragging, the sidebar source fades in place (the opaque held card is the moving copy).
        <div ref={rootRef} className={cn('mt-2 transition-opacity', isDragging && 'opacity-50')}>
          <FormDesignerElementListItem element={element} isDisabled={dragDisabled} disabledTooltipContent={disabledTooltipContent} />
        </div>
      )}
    </ToolboxDraggableItem>
  );
}

function ConfiguratorPanel({ formElement, onUpdateConfig }: ConfigPanelProps) {
  if (!formElement) {
    return (
      <div className="text-secondary-text flex p-2 text-xs font-normal">
        <span>Select a field in the form editor to view options</span>
      </div>
    );
  }

  const Configurator = definitionFor(formElement.kind).configurator;

  if (!Configurator) {
    return (
      <div className="text-grey-4 flex h-full items-center justify-center">
        <p>This widget has no configuration options</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Configurator element={formElement} onChange={(updates) => onUpdateConfig(formElement.id, updates)} />
    </div>
  );
}

function FormDesignerJSONPreview({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const form = useFormContext<FormDesignerForm>();

  const formData = form.watch();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Form JSON Preview</DialogTitle>
        </DialogHeader>
        <div className="relative max-h-[70vh] overflow-auto rounded-md">
          <Highlight
            theme={themes.nightOwl}
            code={(() => {
              const submitValues = {
                name: formData.name,
                elements: formData.elements,
              };
              return JSON.stringify(submitValues, null, 2);
            })()}
            language="json">
            {({ tokens, getLineProps, getTokenProps }) => (
              <pre className="p-4 text-sm">
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line })}>
                    <span className="mr-4 inline-block w-8 text-right opacity-50 select-none">{i + 1}</span>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </div>
                ))}
              </pre>
            )}
          </Highlight>
        </div>
      </DialogContent>
    </Dialog>
  );
}
